import React, { useState } from 'react';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import DataFilterList from '@pages-clean/admin/study/admin-data-views/filters/data-filters/list/data-filter-list';
import {
    ChipValues,
    DataFilterResultType,
    ExtraDataFields,
} from '@pages-clean/admin/study/admin-data-views/filters/types';
import { IUser } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import { StudyFields } from '@queries/generated/fragments.generated';

type FiltersProps = {
    user: IUser;
    study: StudyFields;
    filtersData: DataFilterResultType[];
    siteAndSubjectFilters: DataFilterResultType[];
    isLoading: boolean;
    onDataFilterChange: (val: DataFilterResultType[]) => void;
    onSiteSubjectFilterChange: (val: DataFilterResultType[]) => void;
    extraDataFields?: ExtraDataFields;
};

const Filters = ({
    user,
    study,
    filtersData,
    siteAndSubjectFilters,
    isLoading,
    onDataFilterChange,
    onSiteSubjectFilterChange,
    extraDataFields,
}: FiltersProps) => {
    const { t } = useTranslation();
    const [chip, setChip] = useState<ChipValues>(ChipValues.SiteFilters);
    const canEdit = user.canWriteSystemAdminLevel?.(false, false, false, true) ?? false;
    const { anyComponentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const updateChip = async (tabIndex: number) => {
        if (anyComponentHasChanges() && tabIndex !== null) {
            const confirmed = await confirmDiscardChanges('data-view-filter-unsaved-confirmation');
            if (!confirmed) {
                return;
            }
        }
        if (tabIndex !== null) {
            setChip(tabIndex);
        }
    };
    return (
        <Card
            data-testid="editable-data-view-config-filters"
            title={
                <ChipList
                    // We use a compound key here to prevent a bug with the focus state of the chiplist causing
                    // some rerenders to steal focus from input elements
                    key={String(anyComponentHasChanges())}
                    data-testid="filters-chip-list"
                    data={[
                        { text: t('admin:siteFilters'), value: ChipValues.SiteFilters },
                        { text: t('admin:subjectFilters'), value: ChipValues.SubjectFilters },
                        { text: t('admin:filters.dataFilters'), value: ChipValues.DataFilters },
                    ]}
                    value={chip}
                    onChange={async (event) => {
                        await updateChip(event.value as ChipValues);
                    }}
                    selection="single"
                />
            }
        >
            <div style={{ height: 'calc(100vh - 215px - var(--page-banners-height)' }}>
                <DataFilterList
                    study={study}
                    siteAndSubjectFilters={siteAndSubjectFilters}
                    filtersData={filtersData}
                    isLoading={isLoading}
                    canEdit={canEdit}
                    onDataFilterChange={onDataFilterChange}
                    onSiteSubjectFilterChange={onSiteSubjectFilterChange}
                    user={user}
                    selectedChip={chip}
                    key={chip}
                    extraDataFields={extraDataFields}
                />
            </div>
        </Card>
    );
};
export default Filters;
